import { useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, Eye, EyeOff, Flag, Send, Trash2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useConfirm } from '@/components/ui/confirm';
import { useHasAnyRole } from '@/auth/hooks';
import { AppRole } from '@/auth/roles';
import { http } from '@/api/http';
import { useContentModerationOverride, useUpdatePostStatus } from '../api';
import { isPostHidden, normalizePostStatusKey } from '../lib/contentDisplay';
import { SystemNotificationDialog } from '@/features/users/components/SystemNotificationDialog';
import { RejectWithPoliciesDialog } from './RejectWithPoliciesDialog';

interface ContentModerationActionsCardProps {
  contentId: string;
  postStatus?: string | null | undefined;
  moderationStatus?: string | null | undefined;
  creatorId?: number | null | undefined;
  /** Pipeline Test harness rows must not be restored/published to the feed. */
  adminPipelineHarness?: boolean | undefined;
}

/**
 * Full moderation actions panel for content detail.
 * Provides all moderative actions including "Report to Coop" which submits
 * content for the highest tier of automated + human review.
 * PENDING / ERROR / held (WAT) get Manual Override Publish / Reject (scan in flight
 * or failed); HUMAN_REVIEW ledger offers Approve / Reject.
 */
export function ContentModerationActionsCard({
  contentId,
  postStatus,
  moderationStatus,
  creatorId,
  adminPipelineHarness = false,
}: ContentModerationActionsCardProps) {
  const confirm = useConfirm();
  const updateStatus = useUpdatePostStatus(contentId);
  const overrideMut = useContentModerationOverride(contentId);
  const [isReporting, setIsReporting] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  const canModerate = useHasAnyRole([
    AppRole.Administrator,
    AppRole.SystemAdmin,
    AppRole.ContentManager,
  ]);

  if (!canModerate) return null;

  const hidden = isPostHidden(postStatus);
  const isTerminated = normalizePostStatusKey(postStatus) === 'TER';
  const isHeld = normalizePostStatusKey(postStatus) === 'WAT';
  const restoreBlocked = (hidden || isTerminated) && adminPipelineHarness;
  const publishBlocked = adminPipelineHarness;
  const ledger = (moderationStatus ?? '').trim().toUpperCase();
  const needsHumanReview = ledger === 'HUMAN_REVIEW';
  /** Scan in flight, failed, or still on pre-publish hold — human may override automation. */
  const canManualOverride =
    !isTerminated &&
    !needsHumanReview &&
    (ledger === 'PENDING' || ledger === 'ERROR' || isHeld);
  const deciding = overrideMut.isPending;

  async function onApproveHumanReview() {
    const { confirmed } = await confirm({
      title: 'Approve Content',
      description:
        'Approve this Human Review item. The post will leave the review queue and become eligible for publication.',
      confirmText: 'Approve',
    });
    if (!confirmed) return;
    try {
      await overrideMut.mutateAsync({ action: 'FORCE_APPROVE' });
      toast.success('Content approved.');
    } catch {
      toast.error('Failed to approve content.');
    }
  }

  async function onManualPublish() {
    if (publishBlocked) {
      toast.error('Pipeline Test harness content cannot be published.');
      return;
    }
    const { confirmed } = await confirm({
      title: 'Publish Content',
      description:
        'Publish this post now and close any open Coop case as approved. ' +
        'This overrides the automated scan — use when you do not want to wait for the scan result.',
      confirmText: 'Publish',
    });
    if (!confirmed) return;
    try {
      await overrideMut.mutateAsync({
        action: 'FORCE_APPROVE',
        reason: 'Admin manual publish override',
      });
      toast.success('Content published.');
    } catch {
      toast.error('Failed to publish content.');
    }
  }

  async function onRejectWithPolicies(result: {
    violatedPolicies: string[];
    notes?: string;
  }) {
    try {
      await overrideMut.mutateAsync({
        action: 'FORCE_REJECT',
        violatedPolicies: result.violatedPolicies,
        ...(result.notes
          ? { reason: result.notes }
          : canManualOverride
            ? { reason: 'Admin manual reject override' }
            : {}),
      });
      setRejectOpen(false);
      toast.success('Content rejected.');
    } catch {
      toast.error('Failed to reject content.');
    }
  }

  async function onTakeDown() {
    const { confirmed } = await confirm({
      title: 'Take Down Content',
      description:
        'This will hide the content from all users and close any open Coop Review Console job.',
      confirmText: 'Take Down',
      variant: 'destructive',
    });
    if (!confirmed) return;
    try {
      // Enforcement path updates TipMi ledger + writes back to Coop Review Console.
      await overrideMut.mutateAsync({ action: 'FORCE_REJECT', reason: 'Admin take down' });
      toast.success('Content taken down.');
    } catch {
      toast.error('Failed to take down content.');
    }
  }

  async function onRestore() {
    if (restoreBlocked) {
      toast.error('Pipeline Test harness content cannot be published.');
      return;
    }
    const { confirmed } = await confirm({
      title: 'Restore Content',
      description:
        'This will make the content visible again and resolve the Coop Review Console case as approved.',
      confirmText: 'Restore',
    });
    if (!confirmed) return;
    try {
      await overrideMut.mutateAsync({ action: 'FORCE_APPROVE', reason: 'Admin restore / re-list' });
      toast.success('Content restored.');
    } catch {
      toast.error('Failed to restore content.');
    }
  }

  async function onTerminate() {
    const { confirmed, reason } = await confirm({
      title: 'Permanently Delete Content',
      description:
        'This permanently terminates the content and closes any open Coop Review Console job. This action cannot be undone.',
      confirmText: 'Delete Permanently',
      variant: 'destructive',
      requireReason: true,
    });
    if (!confirmed) return;
    try {
      await overrideMut.mutateAsync({
        action: 'FORCE_REJECT',
        reason: reason?.trim() || 'Admin terminate',
      });
      // TER is TipMi-only permanent deletion after enforcement HID + Coop close.
      await updateStatus.mutateAsync({ postId: Number(contentId), params: { postStatus: 'TER' } });
      toast.success('Content permanently deleted.');
    } catch {
      toast.error('Failed to delete content.');
    }
  }

  async function onReportToCoop() {
    const { confirmed } = await confirm({
      title: 'Report to Highest-Tier Review',
      description:
        'This submits the content for the highest tier of moderation review (human + automated). ' +
        'It will be treated as a priority escalation.',
      confirmText: 'Submit for Review',
      variant: 'destructive',
    });
    if (!confirmed) return;

    setIsReporting(true);
    try {
      await http.post('/admin/api/moderation/report/admin-escalate', {
        contentId: Number(contentId),
        contentType: 'P',
        reason: 'Admin-initiated highest-tier review',
      });
      toast.success('Content submitted for highest-tier review.');
    } catch {
      toast.error('Failed to submit for review.');
    } finally {
      setIsReporting(false);
    }
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <AlertTriangle className="size-4 text-amber-500" />
            Moderation Actions
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {canManualOverride && (
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground">Manual Override</p>
              <p className="text-[11px] text-muted-foreground">
                Override while the scan is still running or has failed. Applies immediately.
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="default"
                  className="flex-1 bg-emerald-600 hover:bg-emerald-600/90"
                  disabled={deciding || publishBlocked}
                  title={
                    publishBlocked
                      ? 'Pipeline Test harness content cannot be published.'
                      : undefined
                  }
                  onClick={() => void onManualPublish()}
                >
                  <CheckCircle2 className="mr-2 size-3.5" />
                  Publish
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  className="flex-1"
                  disabled={deciding}
                  onClick={() => setRejectOpen(true)}
                >
                  <XCircle className="mr-2 size-3.5" />
                  Reject
                </Button>
              </div>
            </div>
          )}

          {needsHumanReview && (
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground">Human Review Decision</p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="default"
                  className="flex-1"
                  disabled={deciding || isTerminated}
                  onClick={() => void onApproveHumanReview()}
                >
                  <CheckCircle2 className="mr-2 size-3.5" />
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  className="flex-1"
                  disabled={deciding || isTerminated}
                  onClick={() => setRejectOpen(true)}
                >
                  <XCircle className="mr-2 size-3.5" />
                  Reject
                </Button>
              </div>
            </div>
          )}

          {/* Report to Coop (highest tier) */}
          <div className="space-y-1.5">
            <p className="text-xs font-medium text-muted-foreground">Escalation</p>
            <Button
              size="sm"
              variant="destructive"
              className="w-full"
              disabled={isReporting || isTerminated || needsHumanReview}
              onClick={() => void onReportToCoop()}
            >
              <Flag className="mr-2 size-3.5" />
              Report for Highest-Tier Review
            </Button>
          </div>

          {/* Visibility controls */}
          <div className="space-y-1.5">
            <p className="text-xs font-medium text-muted-foreground">Visibility</p>
            <div className="flex gap-2">
              {hidden || isTerminated ? (
                <Button
                  size="sm"
                  variant="default"
                  className="flex-1"
                  disabled={updateStatus.isPending || isTerminated || restoreBlocked}
                  title={
                    restoreBlocked
                      ? 'Pipeline Test harness content cannot be published.'
                      : undefined
                  }
                  onClick={() => void onRestore()}
                >
                  <Eye className="mr-2 size-3.5" />
                  Restore
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="secondary"
                  className="flex-1"
                  disabled={updateStatus.isPending}
                  onClick={() => void onTakeDown()}
                >
                  <EyeOff className="mr-2 size-3.5" />
                  Take Down
                </Button>
              )}
              <Button
                size="sm"
                variant="destructive"
                className="flex-1"
                disabled={updateStatus.isPending || isTerminated}
                onClick={() => void onTerminate()}
              >
                <Trash2 className="mr-2 size-3.5" />
                Delete
              </Button>
            </div>
          </div>

          {/* Communication */}
          <div className="space-y-1.5">
            <p className="text-xs font-medium text-muted-foreground">Communication</p>
            <Button
              size="sm"
              variant="outline"
              className="w-full"
              disabled={!creatorId}
              onClick={() => setNotifyOpen(true)}
            >
              <Send className="mr-2 size-3.5" />
              Send Notification to Creator
            </Button>
          </div>
        </CardContent>
      </Card>

      <SystemNotificationDialog
        open={notifyOpen}
        onOpenChange={setNotifyOpen}
        userId={creatorId}
      />
      <RejectWithPoliciesDialog
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        pending={deciding}
        onConfirm={onRejectWithPolicies}
      />
    </>
  );
}
